import { IonIcon, IonSkeletonText } from '@ionic/react';
import { addCircle } from 'ionicons/icons';
import React from 'react';

const StatsLoadingSkeleton: React.FC = () => {
    return (
        <div className="min-h-full pb-8 bg-black">
            {/* Spider Chart Section Skeleton */}
            <div className="px-4 pt-2 pb-4">
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 shadow-2xl border border-gray-700">
                    {/* Filter Headers Skeleton */}
                    <div className="flex flex-row items-center justify-between mb-4">
                        <div className="w-24 h-10 bg-gray-700 rounded-lg">
                            <IonSkeletonText animated style={{ width: '100%', height: '100%' }} />
                        </div>
                        <div className="w-28 h-10 bg-gray-700 rounded-lg">
                            <IonSkeletonText animated style={{ width: '100%', height: '100%' }} />
                        </div>
                    </div>

                    {/* Spider Chart Area Skeleton */}
                    <div className="relative flex items-center justify-center" style={{ height: '375px' }}>
                        {/* Central circle representing the chart */}
                        <div className="relative">
                            <div className="w-48 h-48 rounded-full border-4 border-gray-600 bg-gray-800/50">
                                <IonSkeletonText animated style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                            </div>

                            {/* Chart axis lines skeleton */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-1 h-48 bg-gray-600 absolute opacity-30"></div>
                                <div className="w-48 h-1 bg-gray-600 absolute opacity-30"></div>
                                <div className="w-1 h-48 bg-gray-600 absolute opacity-30 rotate-45"></div>
                                <div className="w-1 h-48 bg-gray-600 absolute opacity-30 -rotate-45"></div>
                            </div>

                            {/* Stats labels around the chart */}
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                                <IonSkeletonText animated style={{ width: '60px', height: '16px' }} />
                            </div>
                            <div className="absolute top-1/2 -right-12 transform -translate-y-1/2">
                                <IonSkeletonText animated style={{ width: '50px', height: '16px' }} />
                            </div>
                            <div className="absolute -bottom-8 right-4">
                                <IonSkeletonText animated style={{ width: '55px', height: '16px' }} />
                            </div>
                            <div className="absolute -bottom-8 left-4">
                                <IonSkeletonText animated style={{ width: '45px', height: '16px' }} />
                            </div>
                            <div className="absolute top-1/2 -left-12 transform -translate-y-1/2">
                                <IonSkeletonText animated style={{ width: '65px', height: '16px' }} />
                            </div>
                            <div className="absolute -top-8 right-4">
                                <IonSkeletonText animated style={{ width: '40px', height: '16px' }} />
                            </div>
                        </div>

                        {/* Legend Skeleton */}
                        <div className="absolute bottom-4 left-4 space-y-2">
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-[#5861AC] rounded-full"></div>
                                <IonSkeletonText animated style={{ width: '80px', height: '14px' }} />
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-gray-600 rounded-full">
                                    <IonSkeletonText animated style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                                </div>
                                <IonSkeletonText animated style={{ width: '90px', height: '14px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Floating Add Comparison Button Skeleton */}
                    <div className="absolute bottom-2 right-2 bg-gray-600 rounded-full p-3 shadow-lg">
                        <IonIcon icon={addCircle} className="text-2xl text-gray-400" />
                    </div>
                </div>
            </div>

            {/* Primary Stats Summary Card Skeleton */}
            <div className="px-4 pb-4">
                <div className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-2xl p-6 shadow-xl border border-gray-600">
                    {/* Card Header */}
                    <div className="mb-4">
                        <IonSkeletonText animated style={{ width: '180px', height: '24px', marginBottom: '8px' }} />
                        <IonSkeletonText animated style={{ width: '120px', height: '16px' }} />
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        {[...Array(6)].map((_, index) => (
                            <div key={index} className="text-center">
                                <div className="bg-gray-700 rounded-lg p-3">
                                    <IonSkeletonText animated style={{ width: '40px', height: '28px', margin: '0 auto 8px' }} />
                                    <IonSkeletonText animated style={{ width: '60px', height: '14px', margin: '0 auto' }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Comparison Stats Cards Skeleton */}
            <div className="px-4 space-y-4">
                {[...Array(2)].map((_, cardIndex) => (
                    <div key={cardIndex} className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-2xl p-6 shadow-xl border border-gray-600">
                        {/* Card Header */}
                        <div className="mb-4">
                            <IonSkeletonText animated style={{ width: '200px', height: '24px', marginBottom: '8px' }} />
                            <IonSkeletonText animated style={{ width: '140px', height: '16px' }} />
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-4">
                            {[...Array(6)].map((_, index) => (
                                <div key={index} className="text-center">
                                    <div className="bg-gray-700 rounded-lg p-3">
                                        <IonSkeletonText animated style={{ width: '40px', height: '28px', margin: '0 auto 8px' }} />
                                        <IonSkeletonText animated style={{ width: '60px', height: '14px', margin: '0 auto' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Subscription Info Section Skeleton */}
            <div className="px-4 pb-4 mt-4">
                <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                    <IonSkeletonText animated style={{ width: '100%', height: '16px', marginBottom: '8px' }} />
                    <IonSkeletonText animated style={{ width: '90%', height: '16px', marginBottom: '8px' }} />
                    <IonSkeletonText animated style={{ width: '85%', height: '16px' }} />
                </div>
            </div>

            {/* How It Works Info Section Skeleton */}
            <div className="px-4 pb-4">
                <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                    <IonSkeletonText animated style={{ width: '120px', height: '16px', marginBottom: '8px' }} />
                    <IonSkeletonText animated style={{ width: '100%', height: '14px', marginBottom: '6px' }} />
                    <IonSkeletonText animated style={{ width: '95%', height: '14px', marginBottom: '6px' }} />
                    <IonSkeletonText animated style={{ width: '98%', height: '14px', marginBottom: '6px' }} />
                    <IonSkeletonText animated style={{ width: '92%', height: '14px', marginBottom: '6px' }} />
                    <IonSkeletonText animated style={{ width: '88%', height: '14px' }} />
                </div>
            </div>
        </div>
    );
};

export default StatsLoadingSkeleton;